/**
 * Serviço para gerenciar sons de notificações
 * Suporta sons customizados e geração de tons via Web Audio API
 */

export type NotificationSoundType = 
  | 'default' 
  | 'success' 
  | 'error' 
  | 'warning' 
  | 'info' 
  | 'reminder'
  | 'appointment'
  | 'message'
  | 'none';

export interface SoundNotificationSettings {
  enabled: boolean;
  volume: number; // 0.0 a 1.0
  soundType: NotificationSoundType;
  quietHours: {
    enabled: boolean;
    start: string; // HH:mm
    end: string; // HH:mm
  };
}

class SoundNotificationService {
  private audioContext: AudioContext | null = null;
  private settings: SoundNotificationSettings = {
    enabled: true,
    volume: 0.7,
    soundType: 'default',
    quietHours: {
      enabled: false,
      start: '22:00',
      end: '07:00'
    }
  };

  constructor() {
    // Carregar configurações do localStorage
    this.loadSettings();
    
    // Inicializar AudioContext (lazy loading)
    if (typeof window !== 'undefined' && 'AudioContext' in window) {
      try {
        this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      } catch (error) {
        console.warn('⚠️ [SOUND] AudioContext não disponível:', error);
      }
    }
  }

  /**
   * Carregar configurações do localStorage
   */
  private loadSettings(): void {
    try {
      const saved = localStorage.getItem('notificationSoundSettings');
      if (saved) {
        this.settings = { ...this.settings, ...JSON.parse(saved) };
      }
    } catch (error) {
      console.warn('⚠️ [SOUND] Erro ao carregar configurações:', error);
    }
  }

  /**
   * Salvar configurações no localStorage
   */
  saveSettings(settings: Partial<SoundNotificationSettings>): void {
    this.settings = { ...this.settings, ...settings };
    try {
      localStorage.setItem('notificationSoundSettings', JSON.stringify(this.settings));
    } catch (error) {
      console.error('❌ [SOUND] Erro ao salvar configurações:', error);
    }
  }

  /**
   * Obter configurações atuais
   */
  getSettings(): SoundNotificationSettings {
    return { ...this.settings };
  }

  /**
   * Verificar se está em horário silencioso
   */
  private isQuietHours(): boolean {
    if (!this.settings.quietHours.enabled) return false;

    const now = new Date();
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    const { start, end } = this.settings.quietHours;

    // Se o horário silencioso cruza a meia-noite
    if (start > end) {
      return currentTime >= start || currentTime <= end;
    }
    
    return currentTime >= start && currentTime <= end;
  }

  /**
   * Tocar som de notificação
   */
  async playSound(type: NotificationSoundType = 'default'): Promise<void> {
    // Verificar se som está habilitado
    if (!this.settings.enabled || type === 'none') {
      return;
    }

    // Verificar horário silencioso
    if (this.isQuietHours()) {
      console.log('🔇 [SOUND] Horário silencioso ativo, som não reproduzido');
      return;
    }

    // Usar tipo customizado se especificado, senão usar das configurações
    const soundType = type !== 'default' ? type : this.settings.soundType;

    try {
      // Tentar tocar som customizado primeiro
      if (await this.playCustomSound(soundType)) {
        return;
      }

      // Fallback: gerar som via Web Audio API
      await this.generateTone(soundType);
    } catch (error) {
      console.error('❌ [SOUND] Erro ao tocar som:', error);
    }
  }

  /**
   * Tocar som customizado de arquivo
   */
  private async playCustomSound(type: NotificationSoundType): Promise<boolean> {
    try {
      // Mapear tipo para arquivo de som
      const soundFiles: Record<NotificationSoundType, string> = {
        default: '/sounds/notification.mp3',
        success: '/sounds/success.mp3',
        error: '/sounds/error.mp3',
        warning: '/sounds/warning.mp3',
        info: '/sounds/info.mp3',
        reminder: '/sounds/reminder.mp3',
        appointment: '/sounds/appointment.mp3',
        message: '/sounds/message.mp3',
        none: ''
      };

      const soundFile = soundFiles[type];
      if (!soundFile) return false;

      // Tentar carregar e tocar arquivo
      const audio = new Audio(soundFile);
      audio.volume = this.settings.volume;
      
      // Verificar se arquivo existe
      return new Promise((resolve) => {
        audio.oncanplaythrough = () => {
          audio.play().then(() => {
            console.log(`🔊 [SOUND] Som customizado tocado: ${type}`);
            resolve(true);
          }).catch(() => {
            resolve(false);
          });
        };
        
        audio.onerror = () => {
          resolve(false);
        };
        
        // Timeout para verificar se arquivo existe
        setTimeout(() => {
          if (audio.readyState >= 2) {
            audio.play().then(() => resolve(true)).catch(() => resolve(false));
          } else {
            resolve(false);
          }
        }, 100);
      });
    } catch (error) {
      return false;
    }
  }

  /**
   * Gerar tom via Web Audio API
   */
  private async generateTone(type: NotificationSoundType): Promise<void> {
    if (!this.audioContext) {
      // Tentar criar AudioContext novamente
      try {
        this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      } catch (error) {
        console.warn('⚠️ [SOUND] AudioContext não disponível');
        return;
      }
    }

    // Se AudioContext estiver suspenso, retomar
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }

    // Configurações de frequência e duração por tipo
    const toneConfig: Record<NotificationSoundType, { frequency: number; duration: number; pattern?: number[] }> = {
      default: { frequency: 800, duration: 200 },
      success: { frequency: 600, duration: 150, pattern: [600, 800] },
      error: { frequency: 400, duration: 300, pattern: [400, 300] },
      warning: { frequency: 500, duration: 200, pattern: [500, 600] },
      info: { frequency: 700, duration: 150 },
      reminder: { frequency: 600, duration: 200, pattern: [600, 700, 600] },
      appointment: { frequency: 800, duration: 150, pattern: [800, 1000] },
      message: { frequency: 700, duration: 100 },
      none: { frequency: 0, duration: 0 }
    };

    const config = toneConfig[type];
    if (!config || config.frequency === 0) return;

    try {
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      // Configurar volume
      gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(this.settings.volume * 0.3, this.audioContext.currentTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + config.duration / 1000);

      // Configurar frequência
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(config.frequency, this.audioContext.currentTime);

      // Tocar padrão se especificado
      if (config.pattern && config.pattern.length > 1) {
        config.pattern.forEach((freq, index) => {
          const time = this.audioContext!.currentTime + (index * config.duration / 1000);
          oscillator.frequency.setValueAtTime(freq, time);
        });
      }

      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + (config.duration * config.pattern?.length || config.duration) / 1000);

      console.log(`🔊 [SOUND] Tom gerado: ${type} (${config.frequency}Hz)`);
    } catch (error) {
      console.error('❌ [SOUND] Erro ao gerar tom:', error);
    }
  }

  /**
   * Testar som
   */
  async testSound(type: NotificationSoundType = 'default'): Promise<void> {
    await this.playSound(type);
  }

  /**
   * Habilitar/desabilitar som
   */
  setEnabled(enabled: boolean): void {
    this.saveSettings({ enabled });
  }

  /**
   * Definir volume
   */
  setVolume(volume: number): void {
    const clampedVolume = Math.max(0, Math.min(1, volume));
    this.saveSettings({ volume: clampedVolume });
  }

  /**
   * Definir tipo de som padrão
   */
  setSoundType(type: NotificationSoundType): void {
    this.saveSettings({ soundType: type });
  }
}

// Exportar instância singleton
export const soundNotificationService = new SoundNotificationService();

